<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import PoolConnector from "../pool/PoolConnector.vue";
import ChargemapSkeletonLoader from "./ChargemapSkeletonLoader.vue";
const props = defineProps({
  chargingConnectors: {
    type: Array,
    required: true
  },
  legacyApiUrl: {
    type: String,
    required: true
  },
  loadingStations: {
    type: Boolean,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  isB2B: {
    type: Boolean,
    default: true
  }
});
const emits = defineEmits(["connectorClicked"]);
const isMousePressed = ref(false);
const isMouseDragging = ref(false);
const scrollLeftValue = ref(0);
const startX = ref(0);
const connectorsList = ref(null);
const canScrollRight = ref(true);
const canScrollLeft = ref(false);
const timeout = ref(null);
const orderedConnectors = computed(
  () => [...props.chargingConnectors].sort(
    (a, b) => a.powerMax < b.powerMax ? 1 : -1
  ).sort(
    (a, b) => a.availableCount < b.availableCount ? 1 : -1
  )
);
const calculateCanScroll = (scrollValue = 0) => {
  if (connectorsList.value === null) {
    return;
  }
  canScrollRight.value = connectorsList.value.scrollLeft + scrollValue + connectorsList.value.clientWidth <= connectorsList.value.scrollWidth && orderedConnectors.value.length > 4 && !props.loadingStations;
  canScrollLeft.value = connectorsList.value.scrollLeft + scrollValue > 0 && orderedConnectors.value.length > 4 && !props.loadingStations;
};
const scroll = (e) => {
  calculateCanScroll(e.target.scrollLeft);
};
const startDragging = (e) => {
  if (connectorsList.value === null) {
    return;
  }
  isMousePressed.value = true;
  startX.value = e.clientX || (e.touches?.[0]?.clientX || 0) - connectorsList.value.offsetLeft;
  scrollLeftValue.value = connectorsList.value.scrollLeft;
};
const mouseLeave = () => {
  isMousePressed.value = false;
  isMouseDragging.value = false;
};
const stopDragging = () => {
  isMousePressed.value = false;
  clearTimeout(timeout.value);
  timeout.value = setTimeout(() => {
    isMouseDragging.value = false;
  }, 1);
};
const move = (e) => {
  if (isMousePressed.value || connectorsList.value === null) {
    return;
  }
  const x = e.clientX || (e.touches?.[0]?.clientX || 0) - connectorsList.value.offsetLeft;
  const localScroll = x - startX.value;
  if (x !== startX.value) {
    isMouseDragging.value = true;
  } else {
    return;
  }
  connectorsList.value.scrollLeft = scrollLeftValue.value - localScroll;
  calculateCanScroll(-localScroll);
};
const scrollRight = (e) => {
  if (connectorsList.value === null) {
    return;
  }
  e.stopPropagation();
  connectorsList.value.scrollLeft += 100;
  calculateCanScroll(100);
};
const scrollLeft = (e) => {
  if (connectorsList.value === null) {
    return;
  }
  e.stopPropagation();
  connectorsList.value.scrollLeft -= 100;
  calculateCanScroll(-100);
};
const connectorClicked = (connector) => {
  if (!isMouseDragging.value) {
    emits("connectorClicked", connector);
  }
};
onMounted(() => {
  calculateCanScroll();
  window.addEventListener("resize", () => calculateCanScroll());
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", () => calculateCanScroll());
});
</script>

<template>
    <div
        class="relative"
        :class="{
  'select-none': isMousePressed
}"
    >
        <div
            ref="connectorsList"
            class="hidden-scrollbar w-full flex gap-2.5 min-h-14 overflow-y-auto pl-2 pr-4 md:pr-6 cursor-grab"
            :class="{
  'cursor-grabbing': isMousePressed,
  'scroll-smooth': !isMousePressed
}"
            @mousedown="startDragging"
            @touchstart="startDragging"
            @mouseup="stopDragging"
            @mouseleave="mouseLeave"
            @scroll="scroll"
            @touchend="stopDragging"
            @mousemove="move"
            @touchmove="move"
        >
            <div
                v-if="loadingStations"
                class="flex-shrink-0 px-4 gap-3 select-none flex-row flex"
            >
                <ChargemapSkeletonLoader
                    v-for="index in 5"
                    :key="index"
                    class="h-16 w-24 flex items-stretch justify-center rounded-lg"
                />
            </div>
            <div
                v-for="chargingConnector of orderedConnectors"
                v-else
                :key="`${chargingConnector.connectorType.id}`"
                class="flex-shrink-0 p-1"
            >
                <PoolConnector
                    :total-connectors-count="chargingConnector.count"
                    :available-connectors-count="
  chargingConnector.availableCount
"
                    :is-all-static="false"
                    :is-all-unknown="false"
                    :is-all-faulty="false"
                    :power-connector="chargingConnector.powerMax"
                    :connector-type="chargingConnector.connectorType"
                    :legacy-api-url="legacyApiUrl"
                    :disabled
                    :isB2B
                    @click="connectorClicked(chargingConnector)"
                />
            </div>
        </div>
        <button
            class="size-8 hidden md:flex left-1.5 top-4 absolute bg-zinc-100 rounded-full justify-center items-center shadow transition-all duration-500 ease-in-out"
            :style="{
  opacity: canScrollLeft ? 1 : 0,
  pointerEvents: canScrollLeft ? 'all' : 'none'
}"
            @click="scrollLeft"
        >
            <IconChevronLeft class="size-5" />
        </button>
        <button
            class="size-8 hidden md:flex right-2.5 top-4 absolute bg-zinc-100 rounded-full justify-center items-center shadow transition-all duration-500 ease-in-out"
            :style="{
  opacity: canScrollRight ? 1 : 0,
  pointerEvents: canScrollRight ? 'all' : 'none'
}"
            @click="scrollRight"
        >
            <IconChevronRight class="size-5" />
        </button>
    </div>
</template>

<style scoped>
.hidden-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  -webkit-overflow-scrolling: touch;
}
.hidden-scrollbar::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 0px;
  display: none;
}
</style>
